<template>
  <component
    :is="componentName"
    ref="root"
    type="event"
    :add-to-cart-url="addToCartUrl"
    :resource.prop="event"
    :payment="paymentAllowed"
    :hide-review-participant-change="hideReviewParticipantChange"
    :show-participant-form-group-labels="showParticipantFormGroupLabels"
    :hide-review-payment-change="hideReviewPaymentChange"
    :open="open"
    :activator-text="activatorText"
    :mobile-cta-activator-text="mobileCtaActivatorText"
    :resource-error="hasError"
    :load-error-message="errorMessage"
    :retry-activator-text="retryActivatorText"
    review
    mobile-cta-target
    @update-open="handleModalUpdateOpen"
    @retry="handleRetry"
  >
    <div slot="form">
      <sp-card-content-overlay slot="content-overlay" visible>
        <sp-progress v-if="!hasError" type="ellipsis" size="x-small"></sp-progress>
        <sp-alert v-else type="error" append-align="center" class="registration-error" part="registration-error">
          <span class="registration-error__message">{{ resolvedErrorMessage }}</span>
          <sp-button slot="append" class="registration-error__retry" color="primary" @click="handleRetry">
            {{ resolvedRetryActivatorText }}
          </sp-button>
        </sp-alert>
      </sp-card-content-overlay>
    </div>
  </component>
</template>

<script>
const componentTypes = {
  checkout: {
    component: "sp-checkout",
  },
  waitlist: {
    component: "sp-waitlist",
  },
};
</script>

<script setup>
/**
 * This component is used to display the event registration form in a modal.
 * It utilizes the `sp-checkout` component to display the event details and registration form.
 */
import { computed, ref, useShadowRoot, useTemplateRef, watch } from "vue";
import { reportClientError } from "#ce/utils/error-report";
import { toBoolean, toJSON } from "#ce/utils/props";
import { props as propsDefinitions } from "./props";
import { createEventApi } from "./api/event";

const props = defineProps(propsDefinitions);

const api = createEventApi();

const componentType = computed(() => componentTypes[props.type]);
const componentName = computed(() => componentType.value?.component);

const root = useTemplateRef("root");
const open = ref(toBoolean(props.open));
const event = ref(null);
// Drives the failure UI in two places: this component's own form-slot overlay
// (which sp-waitlist renders immediately) and, via `resource-error`, sp-checkout's
// loading overlay (which is gated behind the purchase token). Either way the modal
// shows a retry instead of spinning forever when the event/form fails to load.
const hasError = ref(false);

const shadowRoot = useShadowRoot();

const resolvedErrorMessage = computed(
  () => props.errorMessage || "Sorry, we couldn't load the registration form. Please try again.",
);
const resolvedRetryActivatorText = computed(() => props.retryActivatorText || "Try again");

const addToCartUrl = computed(() => {
  if (!event.value) {
    return;
  }

  return `/purchases/add_to_cart?resource_type=Event&resource_id=${event.value.id}`;
});

const paymentAllowed = computed(() => event.value?.is_online_payment_allowed);

function handleModalUpdateOpen({ detail }) {
  const [value] = detail;
  open.value = value;
}

/**
 * Lazily fetches the event data when the modal is opened.
 * If the event data is already present, it will not fetch it again.
 */
watch(open, async (value) => {
  if (value && !event.value) {
    await fetchEvent();
  }
});

async function fetchEvent() {
  hasError.value = false;

  try {
    const data = await api.fetchEvent(props.url);
    const formElements = buildFormElements(data.registration_form);

    // Swap the slot first, then commit the event only once hydration succeeds, so
    // a failed fetch, malformed payload, or hydration error never leaves the modal
    // half-open with a usable add-to-cart URL but no rendered form.
    hydrateFormSlot(formElements);
    event.value = data.attributes;
  } catch (error) {
    hasError.value = true;
    // Report to the Sparkle origin (props.url), not the page origin — this component
    // also runs embedded on non-Sparkle host pages, where a client-side failure
    // otherwise leaves no server-side trace at all.
    reportClientError("Event registration modal failed to load the registration form", {
      description: `${props.url} — ${error}`,
      baseUrl: props.url,
    });
  }
}

/**
 * Retries the fetch after a failure, restoring the loading state.
 */
function handleRetry() {
  fetchEvent();
}

/**
 * Builds the form elements from the registration form payload, appending the
 * required CSS. Throws if the payload is missing or does not contain a form,
 * so the caller can surface an error instead of hanging on the spinner.
 * @param registrationForm - The registration form JSON string from the API
 * @returns {HTMLElement} - The prepared form elements
 */
function buildFormElements(registrationForm) {
  // convert the escaped HTML string to an unescaped string
  const registrationFormHTML = toJSON(registrationForm);
  const formElements = extractFormElements(registrationFormHTML);

  if (!formElements) {
    throw new Error("Registration form markup is missing a .form-builder-container element");
  }

  loadFormBuilderCSS();
  formElements.classList.add("--registration-form");

  return formElements;
}

function loadFormBuilderCSS() {
  const href = props.supportCSSUrl;

  // The URL is normally supplied as a component default; guard against a missing
  // value (avoids a request to `.../undefined`) and against a duplicate <link>
  // appended by a retry.
  if (!href || shadowRoot?.querySelector(`link[href="${href}"]`)) {
    return;
  }

  const styleLinkTag = document.createElement("link");
  styleLinkTag.rel = "stylesheet";
  styleLinkTag.href = href;
  shadowRoot?.appendChild(styleLinkTag);
}

/**
 * Extracts the form elements from the registration form HTML string.
 *
 * @param registrationFormHTML - The HTML string of the registration form
 * @returns {HTMLElement} - The form elements extracted from the HTML string
 */
function extractFormElements(registrationFormHTML) {
  // intermedate step to convert the form string to DOM elements
  const container = document.createElement("div");
  container.innerHTML = registrationFormHTML;

  // extract the form elements from the container
  return container.querySelector(".form-builder-container");
}

/**
 * Hydrates the form slot with the form elements.
 * @param formElements - The form elements to hydrate the form slot with
 */
function hydrateFormSlot(formElements) {
  const slot = root.value.querySelector("[slot=form]");

  formElements.setAttribute("slot", "form");
  root.value.replaceChild(formElements, slot);
}
</script>

<style>
.registration-error {
  max-width: 24rem;
  margin-inline: auto;
}
</style>
